import { useEffect, useRef, useState } from 'react';
import { Logger } from '@futrli/shared-services';
import { fetchUserConsentData, UserConsentData } from '../../api/sxp';
import { useOneTrust } from './useOneTrust';
import { useWatchForAndCloseCookieBanner } from './useWatchForAndCloseCookieBanner';

const PERFORMANCE_COOKIES_PURPOSE_ID = 'e61f2064-9c0f-4f06-9ea5-8dc14fe4b73a';
const FUNCTIONAL_COOKIES_PURPOSE_ID = 'cb4ff222-0df0-4381-a18a-723117cf7ce8';
const TARGETTING_COOKIES_PURPOSE_ID = '470dd757-fda5-4b61-b3a3-fbfb4d221f4f';

const PERFORMANCE_COOKIES_CATEGORY_ID = 'C0002';
const FUNCTIONAL_COOKIES_CATEGORY_ID = 'C0003';
const TARGETTING_COOKIES_CATEGORY_ID = 'C0004';

/**
 * Takes the UserConsentData from API endpoint and returns an object with useable prefs values in.
 * Each prefs value will either be a string ('1' or '0') if the pref has been set, or undefined if
 * the pref has not yet been set.
 * @param data - UserConsentData retrieved from SXP endpoint
 */
const getPrefs = (data: UserConsentData) => {
  const performance = data.Purposes.find(
    p => p.Id === PERFORMANCE_COOKIES_PURPOSE_ID || p.Label.includes('cookieTypes.performanceAndAnalytical')
  );
  const functional = data.Purposes.find(
    p => p.Id === FUNCTIONAL_COOKIES_PURPOSE_ID || p.Label.includes('cookieTypes.functional')
  );
  const targetting = data.Purposes.find(
    p => p.Id === TARGETTING_COOKIES_PURPOSE_ID || p.Label.includes('cookieTypes.targetingAndAdvertising')
  );

  return {
    performance: performance?.IsAlreadyConsentedOrNot ? Number(performance.Accepted).toString() : undefined,
    functional: functional?.IsAlreadyConsentedOrNot ? Number(functional.Accepted).toString() : undefined,
    targetting: targetting?.IsAlreadyConsentedOrNot ? Number(targetting.Accepted).toString() : undefined
  };
};

/**
 * Takes the prefs values from getPrefs and updates the local OneTrust instance with them
 * @param prefs - User prefs values
 */
const updateOneTrustPrefs = (prefs: ReturnType<typeof getPrefs>) => {
  if (window.OneTrust) {
    Logger.info(`useOneTrustPrefsSync - ${JSON.stringify(prefs)}`);
    try {
      if (prefs.performance) {
        window.OneTrust.UpdateConsent('Category', `${PERFORMANCE_COOKIES_CATEGORY_ID}:${prefs.performance}`);
      }
      if (prefs.functional) {
        window.OneTrust.UpdateConsent('Category', `${FUNCTIONAL_COOKIES_CATEGORY_ID}:${prefs.functional}`);
      }
      if (prefs.targetting) {
        window.OneTrust.UpdateConsent('Category', `${TARGETTING_COOKIES_CATEGORY_ID}:${prefs.targetting}`);
      }
    } catch (e) {
      Logger.warn('useOneTrustPrefsSync - Error while syncing cookie preferences');
    }
  } else {
    Logger.warn('useOneTrustPrefsSync - Cannot sync, OneTrust not available');
  }
};

export const useOneTrustPrefsSync = (userId?: string, config?: typeof window.__sbc_config) => {
  const { isReady: isOneTrustReady } = useOneTrust();
  const request = useRef<number>();
  const [watchForCookieBanner, setWatchForCookieBanner] = useState(false);

  // Watch for and close banner if we managed to get SXP prefs data
  useWatchForAndCloseCookieBanner(watchForCookieBanner);

  // Load new data from SXP if userId is available and sync data with OneTrust
  useEffect(() => {
    const fetchNewConsentData = async (userId: string) => {
      const thisRequest = (request.current = Date.now());
      try {
        const prefsData = await fetchUserConsentData(userId);
        if (prefsData && thisRequest === request.current) {
          const prefs = getPrefs(prefsData);
          updateOneTrustPrefs(prefs);

          // If we have all three consent prefs from SXP, watch for the banner and close it ASAP.
          if (prefs.performance && prefs.functional && prefs.targetting) {
            setWatchForCookieBanner(true);
          }
        }
      } catch (e) {
        // Failed, do nothing
      }
    };

    if (userId && config && isOneTrustReady) {
      fetchNewConsentData(userId);
    } else {
      request.current = undefined;
    }
  }, [isOneTrustReady, config, userId]);
};
